import { HttpClient, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { LogoutAction, SetTokenAction } from '@tsin-core/actions/auth.action';
import { AuthState } from '@tsin-core/states/auth.state';
import { environment } from '@tsin-env/environment';
import { catchError, switchMap, throwError } from 'rxjs';

// authBaseUrl already ends in `/api`, so this resolves to {authBaseUrl}/auth/refresh => .../api/auth/refresh
const REFRESH_URL = `${environment.authBaseUrl}/auth/refresh`;

export const jwtInterceptor: HttpInterceptorFn = (req, next) => {
  const store: Store = inject(Store);
  const http: HttpClient = inject(HttpClient);

  const token = store.selectSnapshot<string | null>(AuthState.getToken);
  const isRefreshRequest = req.url.includes('/auth/refresh');

  // Attach the current access token to every request except the refresh call itself.
  if (token && token.length > 0 && !isRefreshRequest) {
    req = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  return next(req).pipe(
    catchError((error: any) => {
      const refreshToken = store.selectSnapshot<string | null>(AuthState.getRefreshToken);

      // On any 401 (and only if we have a refresh token and this isn't already the refresh call),
      // exchange the refresh token for a fresh access token, then replay the original request.
      if (error.status === 401 && refreshToken && !isRefreshRequest) {
        return http
          .post<{ accessToken?: string; token?: string; refreshToken?: string }>(REFRESH_URL, { refreshToken })
          .pipe(
            switchMap((response) => {
              const newToken = response.accessToken ?? response.token;
              if (!newToken) {
                return throwError(() => error);
              }

              // Persist the refreshed token(s) so subsequent requests use them.
              store.dispatch(new SetTokenAction({ token: newToken, refreshToken: response.refreshToken }));

              const retryReq = req.clone({
                setHeaders: {
                  Authorization: `Bearer ${newToken}`
                }
              });
              return next(retryReq);
            }),
            catchError(() => {
              // Refresh failed: the session is unrecoverable, so clear it and surface the original error.
              store.dispatch(new LogoutAction());
              return throwError(() => error);
            })
          );
      }

      return throwError(() => error);
    })
  );
};
